import { z } from 'zod';
import type { StoredLosslessNumber } from '../../../stored-json-number';
import { isJsonLosslessNumber, JsonLosslessNumberMarkerSchema } from '../../common.schemas';
import {
    type CloverLoggingRedactionJsonValue,
    CloverLoggingRedactionOutcome,
    type CloverLoggingRedactionNormalizedResult,
    type CloverLoggingRedactionRequestDetails,
    type CloverLoggingRedactionResponseDetails,
} from '../../../types/ipg/clover/clover-logging-redaction.types';

export interface CloverLoggingRedactionLosslessNumberMarker {
    readonly isLosslessNumber: true;
}

export const CloverLoggingRedactionLosslessNumberMarkerSchema: z.ZodType<CloverLoggingRedactionLosslessNumberMarker> =
    JsonLosslessNumberMarkerSchema;

export const CloverLoggingRedactionLosslessNumberSchema: z.ZodType<StoredLosslessNumber> =
    z.custom<StoredLosslessNumber>(isJsonLosslessNumber, {
        message: 'Invalid Clover logging redaction lossless number',
    });

export const CloverLoggingRedactionJsonValueSchema: z.ZodType<CloverLoggingRedactionJsonValue> =
    z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.null(),
        CloverLoggingRedactionLosslessNumberSchema,
        z.array(z.lazy(() => CloverLoggingRedactionJsonValueSchema)),
        z.record(
            z.string(),
            z.lazy(() => CloverLoggingRedactionJsonValueSchema)
        ),
    ]);

export const CloverLoggingRedactionRequestDetailsSchema: z.ZodType<CloverLoggingRedactionRequestDetails> =
    z
        .object({
            payload: CloverLoggingRedactionJsonValueSchema,
        })
        .strict();

export const CloverLoggingRedactionNormalizedResultSchema: z.ZodType<CloverLoggingRedactionNormalizedResult> =
    z
        .object({
            redacted_payload: CloverLoggingRedactionJsonValueSchema,
            omitted_sensitive_key_count: z.number().int().nonnegative(),
        })
        .strict();

export const CloverLoggingRedactionRedactedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLoggingRedactionOutcome.REDACTED>;
    normalized_result: typeof CloverLoggingRedactionNormalizedResultSchema;
}> = z
    .object({
        outcome: z.literal(CloverLoggingRedactionOutcome.REDACTED),
        normalized_result: CloverLoggingRedactionNormalizedResultSchema,
    })
    .strict() satisfies z.ZodType<
    Extract<
        CloverLoggingRedactionResponseDetails,
        {
            readonly outcome: typeof CloverLoggingRedactionOutcome.REDACTED;
        }
    >
>;

export const CloverLoggingRedactionMalformedInputResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverLoggingRedactionOutcome.MALFORMED_INPUT>;
    message: z.ZodString;
    payload: z.ZodOptional<z.ZodUnknown>;
}> = z
    .object({
        outcome: z.literal(CloverLoggingRedactionOutcome.MALFORMED_INPUT),
        message: z.string().min(1),
        payload: z.unknown().optional(),
    })
    .strict() satisfies z.ZodType<
    Extract<
        CloverLoggingRedactionResponseDetails,
        {
            readonly outcome: typeof CloverLoggingRedactionOutcome.MALFORMED_INPUT;
        }
    >
>;

export const CloverLoggingRedactionResponseDetailsSchema: z.ZodType<CloverLoggingRedactionResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverLoggingRedactionRedactedResponseDetailsSchema,
        CloverLoggingRedactionMalformedInputResponseDetailsSchema,
    ]);
